// Nastrój dziecka (patch-32): lista nastrojów z koła, zapis raz dziennie przed lekcją w postępie profilu i widok tygodnia („Mój tydzień”).
// Czysta logika bez DOM i sieci — używają jej ekrany, scalanie postępu (`progressMerge.ts`) i serwer.
import type { HistoryEntry, MoodEntry, MoodId, Progress, TeacherLang } from '../types.ts';
import { addDays, daysBetween, weekdayIndex } from './dates.ts';
import { forGender } from './gender.ts';
import type { Gender } from './gender.ts';

export interface MoodInfo {
  /** Stały identyfikator (zapisany w postępie dziecka, nazwy plików `public/mood/<id>.webp` i nagrań `m.<id>`) — nie zmieniamy go, gdy zmienia się nazwa. */
  id: MoodId;
  /** Nazwa po polsku (gdy bot mówi po polsku); końcówki jak w `lib/gender.ts`: „Szczęśliw{y|a}”. */
  pl: string;
  /** Nazwa po angielsku (gdy bot mówi po angielsku). */
  enName: string;
  /** Wysokość buźki w kapsułce tygodnia: 1 = najgorszy dzień, 5 = najlepszy. */
  level: 1 | 2 | 3 | 4 | 5;
  /** Kolor wycinka koła i kapsułki. */
  color: string;
}

/**
 * Kolejność nastrojów na kole, zgodnie z ruchem wskazówek zegara od góry: najpierw radosne, potem neutralne, na końcu przykre —
 * dziecko szybko znajduje „swoją” stronę koła. Nazwy wg opisu użytkownika (2026-10-16) dla arkusza dziesięciu buźek:
 * radość/szczęście, zadowolenie/życzliwość (`calm`: łagodny uśmiech i szeroko otwarte oczy), zakochanie/miłość, pewność siebie/luz, rozbawienie/ekscytacja,
 * smutek/żal, strach/przerażenie (`worried`: blada niebieskawa buźka), złość/gniew, zaskoczenie/zdziwienie, znużenie/wykończenie.
 * Identyfikatory zostały z pierwszej wersji (`calm`, `worried`) — zmieniają się tylko nazwy, żeby zapisane już nastroje dzieci nadal były poprawne.
 */
export const MOODS: readonly MoodInfo[] = [
  { id: 'happy', pl: 'Szczęśliw{y|a}', enName: 'Happy', level: 5, color: '#f6c62d' },
  { id: 'loved', pl: 'Zakochan{y|a}', enName: 'In love', level: 5, color: '#f48fb1' },
  { id: 'cool', pl: 'Wyluzowan{y|a}', enName: 'Cool', level: 4, color: '#f3993f' },
  { id: 'silly', pl: 'Rozbawion{y|a}', enName: 'Silly', level: 4, color: '#ee6b5f' },
  { id: 'calm', pl: 'Zadowolon{y|a}', enName: 'Content', level: 4, color: '#8984f0' },
  { id: 'surprised', pl: 'Zaskoczon{y|a}', enName: 'Surprised', level: 3, color: '#4fc3b0' },
  { id: 'tired', pl: 'Zmęczon{y|a}', enName: 'Tired', level: 2, color: '#b08ae6' },
  { id: 'worried', pl: 'Przestraszon{y|a}', enName: 'Scared', level: 2, color: '#85bdf7' },
  { id: 'sad', pl: 'Smutn{y|a}', enName: 'Sad', level: 1, color: '#5d93ea' },
  { id: 'angry', pl: 'Zł{y|a}', enName: 'Angry', level: 1, color: '#cf3a3a' },
];

/** Nazwa nastroju w języku bota (EN/PL) — nigdy obie naraz; polska z końcówką wg płci dziecka (brak płci = forma męska). */
export const moodName = (m: Pick<MoodInfo, 'pl' | 'enName'>, lang: TeacherLang, gender: Gender = ''): string => (lang === 'pl' ? forGender(m.pl, gender) : m.enName);

export const MOOD_IDS: readonly MoodId[] = MOODS.map((m) => m.id);
export const isMoodId = (v: unknown): v is MoodId => typeof v === 'string' && (MOOD_IDS as readonly string[]).includes(v);
export const moodInfo = (id: MoodId): MoodInfo => MOODS.find((m) => m.id === id) as MoodInfo;
/** Nastrój, na którym koło stoi na starcie (jak na makiecie: „Spokojny”); nic się nie zapisuje, dopóki dziecko nie zakręci kołem albo nie dotknie buźki. */
export const DEFAULT_MOOD: MoodId = 'calm';
export const moodImage = (id: MoodId): string => `./mood/${id}.webp`;

/** Ile dni nastroju trzymamy w postępie (starsze wypadają przy zapisie i scalaniu). */
export const MAX_MOODS = 400;

export const moodOn = (p: Progress, date: string): MoodEntry | null => p.moods?.[date] ?? null;

/** Zapisuje nastrój na dany dzień (kolejny zapis tego samego dnia go zastępuje). Zwraca nową kopię postępu. */
export function setMood(p: Progress, date: string, mood: MoodId, at: number): Progress {
  const moods: Record<string, MoodEntry> = { ...(p.moods ?? {}), [date]: { mood, at } };
  return { ...p, moods: pruneMoods(moods) };
}

/** Zostawia najnowsze `MAX_MOODS` dni. */
export function pruneMoods(moods: Record<string, MoodEntry>): Record<string, MoodEntry> {
  const dates = Object.keys(moods).sort();
  const keep = dates.slice(Math.max(0, dates.length - MAX_MOODS));
  const out: Record<string, MoodEntry> = {};
  for (const d of keep) out[d] = moods[d] as MoodEntry;
  return out;
}

/** Czy przed lekcją pokazać koło nastroju: raz dziennie — gdy na dziś nie ma jeszcze wpisu. */
export const shouldAskMood = (p: Progress, today: string): boolean => moodOn(p, today) === null;

// --- tydzień ------------------------------------------------------------------------------------------

export const WEEK_LABELS = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'] as const;

export interface WeekDay {
  date: string;
  /** „Pn” … „Nd”. */
  label: (typeof WEEK_LABELS)[number];
  entry: MoodEntry | null;
  today: boolean;
  /** Dzień po dzisiejszym: kapsułka pusta i przygaszona. */
  future: boolean;
  /** Czy tego dnia była zakończona lekcja (z historii postępu). */
  lesson: boolean;
}

/** Poniedziałek tygodnia, w którym leży `date`. */
export const weekStart = (date: string): string => addDays(date, -weekdayIndex(date));

/** Siedem dni (Pn–Nd) tygodnia przesuniętego o `offset` tygodni względem dzisiejszego (0 = ten tydzień, −1 = poprzedni). */
export function weekView(p: Progress, today: string, offset = 0): WeekDay[] {
  const start = addDays(weekStart(today), offset * 7);
  const lessonDates = new Set(p.history.filter((e: HistoryEntry) => e.mode === 'lesson').map((e) => e.date));
  return WEEK_LABELS.map((label, i) => {
    const date = addDays(start, i);
    return { date, label, entry: moodOn(p, date), today: date === today, future: date > today, lesson: lessonDates.has(date) };
  });
}

/**
 * Najstarszy tydzień, do którego w „Moim tygodniu” ma sens wracać: ten, w którym jest najwcześniejszy zapisany nastrój (jako ujemne przesunięcie
 * względem bieżącego tygodnia; 0, gdy nie ma żadnego wpisu). Wcześniej dziecko nie miało jeszcze szansy niczego zapisać, więc strzałkę wstecz blokujemy.
 */
export function oldestWeekOffset(p: Progress, today: string): number {
  const dates = Object.keys(p.moods ?? {});
  if (dates.length === 0) return 0;
  const first = dates.reduce((a, b) => (a < b ? a : b));
  return 0 - Math.max(0, Math.round(daysBetween(weekStart(first), weekStart(today)) / 7)); // „0 −” zamiast minusa, żeby nie wyszło −0
}

/**
 * Od ilu dni dziecko zbiera buźki: liczba dni kalendarzowych od najwcześniejszego zapisanego nastroju do dziś, włącznie z oboma (pierwszy dzień = 1);
 * 0, gdy nie zapisano jeszcze żadnego. Podsumowanie „Najczęściej” czeka, aż minie tydzień (`config.ui.moodSummaryMinDays`), bo z jednego dnia nic nie wynika.
 */
export function moodCollectingDays(p: Progress, today: string): number {
  const dates = Object.keys(p.moods ?? {});
  if (dates.length === 0) return 0;
  const first = dates.reduce((a, b) => (a < b ? a : b));
  return Math.max(1, daysBetween(first, today) + 1);
}

/** Najczęstszy nastrój w podanych dniach (przy remisie — ten, który padł później w tygodniu); null, gdy żaden nie został zapisany. */
export function topMood(days: readonly WeekDay[]): MoodId | null {
  const count = new Map<MoodId, number>();
  let best: MoodId | null = null;
  for (const d of days) {
    if (!d.entry) continue;
    const n = (count.get(d.entry.mood) ?? 0) + 1;
    count.set(d.entry.mood, n);
    if (best === null || n >= (count.get(best) ?? 0)) best = d.entry.mood;
  }
  return best;
}
